import { GetTimeSeriesRequest, MetricType as ToolMetricType, SingleMetricRequest, TimeConfig, TimeRangeType, TimeSeriesResponse, TimeWindow } from "./TimeSeriesTypes";
import { MetricSpecifier, MetricType } from "../../clients/metoro/metrics";
import { Formula } from "../../types/formula";

/**
 * Maps ToolMetricType to MetricType
 */
function mapMetricType(toolType: ToolMetricType): MetricType {
    switch(toolType) {
        case ToolMetricType.Metric:
            return MetricType.Metric;
        case ToolMetricType.Trace:
            return MetricType.Trace;
        case ToolMetricType.Logs:
            return MetricType.Logs;
        case ToolMetricType.KubernetesResource:
            return MetricType.Kubernetes;
        default:
            return MetricType.Metric;
    }
}

/**
 * Converts a SingleMetricRequest to a MetricSpecifier for the MultiMetoroMetricsChart
 */
export function convertToMetricSpecifier(metric: SingleMetricRequest): MetricSpecifier {
    // Convert Record<string, string[]> to Map<string, string[]>
    const filtersMap = new Map<string, string[]>();
    if (metric.filters) {
        Object.entries(metric.filters).forEach(([key, value]) => {
            filtersMap.set(key, value);
        });
    }

    const excludeFiltersMap = new Map<string, string[]>();
    if (metric.excludeFilters) {
        Object.entries(metric.excludeFilters).forEach(([key, value]) => {
            excludeFiltersMap.set(key, value);
        });
    }

    return {
        metricType: mapMetricType(metric.type),
        metricName: metric.metricName || "", 
        aggregation: metric.aggregation,
        filters: filtersMap,
        excludeFilters: excludeFiltersMap,
        splits: metric.splits || [],
        regexes: metric.regexes || [],
        excludeRegexes: metric.excludeRegexes || [],
        bucketSize: metric.bucketSize || 0,
        shouldNotReturn: metric.shouldNotReturn || false,
        functions: [], // This field is required in MetricSpecifier but not in tool request
        jsonPath: metric.jsonPath ? metric.jsonPath : undefined,
        // Note: formulaIdentifier is not part of MetricSpecifier in the client
    };
}

/**
 * Calculates the start and end time from a TimeConfig object
 * Returns [startTimeInSeconds, endTimeInSeconds]
 */
export function getTimeRangeFromConfig(config: TimeConfig): [number, number] {
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    
    if (config.type === TimeRangeType.Absolute) {
        // For absolute time range, parse the RFC3339 timestamps
        let startTime = now - 3600; // Default to 1 hour ago
        let endTime = now;
        
        if (config.start_time) {
            try {
                startTime = Math.floor(new Date(config.start_time).getTime() / 1000);
            } catch (error) {
                console.error('Error parsing start_time:', error);
            }
        }
        
        if (config.end_time) {
            try {
                endTime = Math.floor(new Date(config.end_time).getTime() / 1000);
            } catch (error) {
                console.error('Error parsing end_time:', error);
            }
        }
        
        return [startTime, endTime];
    } else {
        // For relative time range, calculate based on time_period and time_window
        if (!config.time_period || !config.time_window) {
            return [now - 3600, now]; // Default to 1 hour if not specified
        }
        
        let seconds = 0;
        const period = config.time_period;
        
        switch(config.time_window) {
            case TimeWindow.Minutes:
                seconds = period * 60;
                break;
            case TimeWindow.Hours:
                seconds = period * 60 * 60;
                break;
            case TimeWindow.Days:
                seconds = period * 24 * 60 * 60;
                break;
            default:
                seconds = 3600; // Default to 1 hour
        }
        
        return [now - seconds, now];
    }
}

/**
 * Creates a TimeSeriesResponse from a GetTimeSeriesRequest
 * This prepares the data for the MultiMetoroMetricsChart component
 */
export function createTimeSeriesResponse(request: GetTimeSeriesRequest, title?: string): TimeSeriesResponse {
    // Get the time range from the time config
    const [startTime, endTime] = getTimeRangeFromConfig(request.time_config);
    
    // Convert all SingleMetricRequest to MetricSpecifier
    const metricSpecifiers = request.timeseries.map(convertToMetricSpecifier);
    
    return {
        startTime,
        endTime,
        metricSpecifiers,
        title: title || "Time Series Data",
        formulas: request.formulas
    };
}

/**
 * Parses a string containing a get_timeseries_data tool response 
 * and returns a TimeSeriesResponse with proper typing for MultiMetoroMetricsChart
 */
export function parseTimeSeriesResponse(responseContent: string): TimeSeriesResponse | null {
    try {
        const data = JSON.parse(responseContent);
        
        if (!data || typeof data !== 'object') {
            return null;
        }
        
        // If we have a complete TimeSeriesResponse
        if (Array.isArray(data.metricSpecifiers)) {
            return {
                startTime: data.startTime || Math.floor((Date.now() / 1000) - 3600),
                endTime: data.endTime || Math.floor(Date.now() / 1000),
                metricSpecifiers: data.metricSpecifiers,
                title: data.title || "Time Series Data",
                formulas: data.formulas || []
            };
        }
        
        // If we have a GetTimeSeriesRequest instead
        if (data.time_config && Array.isArray(data.timeseries)) {
            return createTimeSeriesResponse(data as GetTimeSeriesRequest);
        }
        
        return null;
    } catch (error) {
        console.error('Error parsing time series data:', error);
        return null;
    }
} 